import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import Section from "./Section";
import SectionForm from "./SectionForm";
import LessonForm from "./LessonForm";

const Sections = (props) => {
  const { courseId } = props;
  const [sections, setSections] = useState(props.sections);
  const [selectedSection, setSelectedSection] = useState(sections[0] || null);
  const [openForm, setOpenForm] = useState(false);
  const [lessonFormId, setLessonFormId] = useState(null);
  const [lessonId, setLessonId] = useState(null);

  const sensors = useSensors(useSensor(PointerSensor));

  const orderSections = async (array) => {
    await fetch(`/courses/${courseId}/sections/sections_order`, {
      method: "PUT",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
      },
      body: JSON.stringify({
        section_ids: array.map((item) => item.id),
      }),
    });
  };

  const deleteSection = async (sectionId) => {
    const response = await fetch(`/courses/${courseId}/sections/${sectionId}`, {
      method: "DELETE",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
      },
    });
    if (response.ok) {
      setSections(sections.filter((section) => section.id !== sectionId));
      setSelectedSection(null);
      setOpenForm(false);
    } else {
      // TODO handle error messages
      console.log("ERROR");
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = sections.findIndex((item) => item.id === active.id);
      const newIndex = sections.findIndex((item) => item.id === over.id);
      const array = arrayMove(sections, oldIndex, newIndex);
      setSections(array);
      orderSections(array);
    }
  };

  const handleNewSection = () => {
    setSelectedSection(null);
    setOpenForm(true);
  };

  const handleDragStart = () => {
    setSelectedSection(null);
  };

  useEffect(() => {
    if (selectedSection) {
      setOpenForm(true);
      setLessonFormId(null);
    }
  }, [selectedSection]);

  useEffect(() => {
    if (lessonFormId) {
      setOpenForm(false);
    }
  }, [lessonFormId]);

  const hadnleSelectedSectionChange = (sectionId) => {
    if (selectedSection && selectedSection.id === sectionId) {
      if (!openForm) {
        setOpenForm(true);
        setLessonFormId(null);
        return;
      }
      setSelectedSection(null);
      return;
    }

    setSelectedSection(
      sections.find((section) => section.id === sectionId) || null,
    );
  };

  if (sections.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[80vh] bg-white">
        {!openForm && (
          <div className="flex flex-col items-center border border-dashed border-gray-300 p-10 text-center max-w-md">
            <div className="text-2xl mb-2">＋</div>
            <h2 className="text-xl font-semibold mb-1">
              Dodaj sekcję, aby rozpocząć
            </h2>
            <p className="text-sm text-gray-500 mb-4">Zbuduj zarys szkolenia</p>

            <button
              className="w-2/3 flex items-center gap-1 border border-primaryYellow text-primaryYellow px-4 py-2 hover:bg-primaryYellow50 transition text-sm"
              onClick={() => handleNewSection()}
            >
              <span className="text-lg">＋</span>
              Dodaj sekcję
            </button>
          </div>
        )}
        {openForm && (
          <SectionForm
            courseId={courseId}
            section={selectedSection}
            sections={sections}
            setSections={setSections}
            setSelectedSection={setSelectedSection}
          />
        )}
      </div>
    );
  } else {
    return (
      <div className="flex gap-10">
        <div className="flex flex-col gap-2 w-1/3 items-center">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            onDragStart={handleDragStart}
          >
            <SortableContext
              items={sections.map((item) => item.id)}
              strategy={verticalListSortingStrategy}
            >
              {sections.map((section) => (
                <Section
                  sectionId={section.id}
                  key={section.id}
                  section={section}
                  selectedSection={selectedSection}
                  courseId={courseId}
                  setLessonFormId={setLessonFormId}
                  setLessonId={setLessonId}
                  deleteSection={deleteSection}
                  hadnleSelectedSectionChange={hadnleSelectedSectionChange}
                />
              ))}
            </SortableContext>
          </DndContext>

          <button
            className="flex items-center gap-1 p-2 w-60 text-primaryYellow justify-center"
            onClick={() => handleNewSection()}
          >
            <span className="text-lg">＋</span>
            Dodaj sekcję
          </button>
        </div>

        {openForm && (
          <SectionForm
            courseId={courseId}
            section={selectedSection}
            sections={sections}
            setSections={setSections}
            setSelectedSection={setSelectedSection}
          />
        )}
        {lessonFormId && (
          <LessonForm
            sectionId={lessonFormId}
            sections={sections}
            setSections={setSections}
            lessonId={lessonId}
            setLessonId={setLessonId}
          />
        )}
      </div>
    );
  }
};

Sections.propTypes = {
  courseId: PropTypes.string,
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      title: PropTypes.string,
      description: PropTypes.string,
      lessons: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
          title: PropTypes.string,
          description: PropTypes.string,
          lesson_type: PropTypes.string,
          file_url: PropTypes.string,
        }),
      ),
    }),
  ),
};

export default Sections;
